import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";

import type { Passport, Person } from "@b2bportal/air-booking-api";
import {
  DesktopLayout,
  FlightPassengerEventTypesV2,
  FlightPassengerSelectorsV2,
  MobileLayout,
  PassportSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { getDateFormat, useI18nContext } from "@hopper-b2b/i18n";
import {
  B2BLoadingPopup,
  BackButton,
  ErrorModal,
  MobileFloatingButton,
} from "@hopper-b2b/ui";
import {
  type ViewportType,
  getDateTimeWithFormat,
  useDeviceTypes,
  useVisualViewport,
} from "@hopper-b2b/utilities";
import clsx from "clsx";

import Arrow from "../../../../assets/client/arrow.svg?react";
import Error from "../../../../assets/client/error.svg";
import { useNubankFeatureFlagsContext } from "../../../../utils/featureFlagsContext";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import {
  getClientAssets,
  getFlightSearchDepartureDate,
  getFlightSearchReturnDate,
} from "../../selectors";
import { PassportInfoForm } from "../PassportInfoForm";
import { ConnectedPriceBreakdownContent } from "../PriceBreakdownDropdown";
import "./styles.scss";

export const PassportInformation = () => {
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  const { t } = useI18nContext();
  const send = useCheckoutSend<TEvent>();
  const preferredDateFormat = getDateFormat();
  const passportRef = useRef<HTMLDivElement>(null);
  const { disableTouchFix } = useNubankFeatureFlagsContext();

  // iOS jitter fix: intercept touchstart on inputs, prevent default,
  // then manually focus with preventScroll:true. Prevents iOS from
  // scrolling the viewport weirdly when switching between fields.
  // See NubankSimpleModal/IOS_KEYBOARD_FIX.md for details.
  useEffect(() => {
    if (!matchesMobile || disableTouchFix) return;

    const root = passportRef.current;
    if (!root) return;

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      ) {
        e.preventDefault();
        target.focus({ preventScroll: true });
      }
    };

    root.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => root.removeEventListener("touchstart", onTouchStart);
  }, [matchesMobile, disableTouchFix]);

  const selectedPassengers = useSelector(
    FlightPassengerSelectorsV2.getAllSelectedUserPassengersParent
  );
  const lastSelectedPassenger = useSelector(
    FlightPassengerSelectorsV2.getLastSelectedPassengerId
  );

  const isLoading = useSelector(PassportSelectors.getIsPassportLoading);
  const passportError = useSelector(PassportSelectors.getPassportError);
  const clientAssets = useSelector(getClientAssets);
  const departureDate = useSelector(getFlightSearchDepartureDate);
  const returnDate = useSelector(getFlightSearchReturnDate);

  const [userIndex, setUserIndex] = useState(() => {
    const index = selectedPassengers.findIndex(
      (person) => person.id === lastSelectedPassenger
    );
    return index === -1 ? 0 : index;
  });

  const [passportInfo, setPassportInfo] = useState<Passport | undefined>(
    selectedPassengers[userIndex]?.passport || undefined
  );
  const [passportExpiryErrorMessage, setPassportExpiryErrorMessage] =
    useState("");
  const [passportNumberErrorMessage, setPassportNumberErrorMessage] =
    useState("");

  const handleGoBack = () => {
    send(FlightPassengerEventTypesV2.CLEAR_PASSENGER_INFORMATION_ERROR);
    send(FlightPassengerEventTypesV2.CLEAR_LAST_SELECTED_PASSENGER_ID);
    // deselect user
    send({
      type: FlightPassengerEventTypesV2.SELECT_PASSENGER,
      passengerId: lastSelectedPassenger,
    });
  };

  const updatePassengerWithPassport = useCallback(
    (person: Person, onContinue?: boolean) =>
      send({
        type: onContinue
          ? Event.UPDATE_PASSPORT_AND_CONTINUE
          : Event.UPDATE_PASSENGER,
        person,
      }),
    [send]
  );

  const isDisabled = useMemo(
    () =>
      !!passportExpiryErrorMessage ||
      !!passportNumberErrorMessage ||
      !passportInfo ||
      !passportInfo?.countryOfIssue ||
      !passportInfo?.expiration ||
      !passportInfo?.number,
    [passportExpiryErrorMessage, passportNumberErrorMessage, passportInfo]
  );

  const handleAddPassportInfo = useCallback(() => {
    const person = selectedPassengers[userIndex];
    if (isDisabled) {
      return null;
    }
    if (
      passportInfo.expiration &&
      (!person?.passport?.expiration ||
        passportInfo.expiration !== person?.passport?.expiration)
    ) {
      passportInfo.expiration = getDateTimeWithFormat(
        passportInfo.expiration,
        preferredDateFormat
      )
        .format("YYYY-MM-DD")
        .replace(/\//g, "-");
    }
    person.passport = passportInfo;

    if (!lastSelectedPassenger && userIndex < selectedPassengers.length - 1) {
      updatePassengerWithPassport(person);
      setPassportInfo(selectedPassengers[userIndex + 1].passport);
      setUserIndex(userIndex + 1);
    }

    if (lastSelectedPassenger || userIndex === selectedPassengers.length - 1) {
      updatePassengerWithPassport(person, true);
      setPassportInfo(undefined);
      send(FlightPassengerEventTypesV2.CLEAR_PASSENGER_INFORMATION_ERROR);
      send(FlightPassengerEventTypesV2.CLEAR_LAST_SELECTED_PASSENGER_ID);
    }
  }, [
    selectedPassengers,
    userIndex,
    isDisabled,
    passportInfo,
    lastSelectedPassenger,
    preferredDateFormat,
    updatePassengerWithPassport,
    send,
  ]);

  const clearError = () => send(Event.CLEAR_PASSPORT_ERROR);

  const searchAgain = () => {
    clearError();
    history.push(`/${PATH_FLIGHTS_SEARCH}`);
  };

  const onVisualViewportChange = (difference: ViewportType) => {
    const elements = document.getElementsByClassName(
      "checkout-passport-information"
    ) as HTMLCollectionOf<HTMLDivElement>;
    const contentElementStyle = elements[0].style;
    contentElementStyle.paddingBottom = `${difference.height}px`;
  };

  useVisualViewport(onVisualViewportChange);

  if (isLoading) {
    return (
      <B2BLoadingPopup
        className="passport-information-loader"
        open={isLoading}
      />
    );
  }

  if (passportError) {
    return (
      <ErrorModal
        open={Boolean(passportError)}
        title={t("checkoutError.genericErrorTitle")}
        subtitle={t("checkoutError.genericErrorSubtitle")}
        primaryButtonText={t("checkoutError.searchAgain")}
        primaryOnClick={searchAgain}
        image={Error}
      />
    );
  }

  const renderBackButton = () => {
    return (
      <BackButton
        className="passport-form-popup-back-button"
        onClick={handleGoBack}
      />
    );
  };

  const content = (
    <>
      <PassportInfoForm
        isMobile={true}
        title={t("passportInfo.title")}
        subtitle={t("passportInfo.subtitle")}
        subtitle2={t("firstAndLastName", {
          index: userIndex + 1,
          totalPassengers: selectedPassengers.length,
          firstName: selectedPassengers[userIndex]?.givenName,
          lastName: selectedPassengers[userIndex]?.surname,
        })}
        passportInfo={passportInfo}
        onPassportInfoChange={(info: Passport) => setPassportInfo(info)}
        passportExpiryError={passportExpiryErrorMessage}
        setPassportExpiryError={setPassportExpiryErrorMessage}
        passportNumberError={passportNumberErrorMessage}
        setPassportNumberError={setPassportNumberErrorMessage}
        departureDate={departureDate}
        returnDate={returnDate}
      />
      <MobileFloatingButton
        className={`save-passport-button ${
          userIndex + 1 < selectedPassengers.length ? "next" : ""
        }`}
        disabled={isDisabled}
        onClick={handleAddPassportInfo}
      >
        {userIndex + 1 < selectedPassengers.length ? <Arrow /> : t("confirm")}
      </MobileFloatingButton>
    </>
  );

  return matchesMobile ? (
    <MobileLayout
      ref={passportRef}
      className={clsx("mobile", "checkout-passport-information")}
      title={t("passportInfo.passport")}
      topLeftButton={renderBackButton()}
      headerElement={t("passportInfo.passport")}
    >
      {content}
    </MobileLayout>
  ) : (
    <DesktopLayout
      clientLogo={clientAssets?.logo}
      rightContent={<ConnectedPriceBreakdownContent />}
    >
      {content}
    </DesktopLayout>
  );
};
